import './style.scss';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';

import { Dropdown } from 'primereact/dropdown';
import {
  create as createResearchKpiGroup,
  deleteById as deleteResearchKpiGroupById,
  getAll as getAllResearchKpiGroups,
  restore as restoreResearchKpiGroupById,
  update as updateResearchKpiGroup,
  getAppliedYears,
  clone as cloneResearchKpiGroups,
} from 'apis/researchKpiGroups.api';
import {
  create as createResearchKpi,
  deleteById as deleteResearchKpiById,
  restore as restoreResearchKpiById,
  update as updateResearchKpi,
} from 'apis/researchKpis.api';
import { Can } from 'components/Can';
import { mapError } from 'utils/func';
import ConfirmDialog from 'components/ConfirmDialog';
import { toast } from 'layout';
import { TOAST } from 'constant';
import ResearchKpiConfirm from './components/ResearchKpiConfirm';
import ResearchKpiForm from './components/ResearchKpiForm';
import ResearchKpiGroupConfirm from './components/ResearchKpiGroupConfirm';
import ResearchKpiGroupForm from './components/ResearchKpiGroupForm';

export default function ResearchKpiGroupViewList() {
  // #region Data
  const { t } = useTranslation();
  const dialogResearchKpiGroupFormRef = useRef(null);
  const dialogResearchKpiGroupConfirmationRef = useRef(null);
  const dialogResearchKpiFormRef = useRef(null);
  const dialogResearchKpiConfirmationRef = useRef(null);
  const [expandedRows, setExpandedRows] = useState(null);
  const [selectAppliedYear, setSelectAppliedYear] = useState(null);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);

  const {
    data: _data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    ['researchKpiGroup', selectAppliedYear],
    ({ queryKey: [, _selectAppliedYear] }) =>
      getAllResearchKpiGroups({
        appliedYear: _selectAppliedYear?.appliedYear,
      }),
    { enabled: !!selectAppliedYear }
  );
  const data = useMemo(() => _data?.data ?? [], [_data]);

  const { data: _appliedYearsData } = useQuery(
    ['researchKpiGroup:getAppliedYears'],
    getAppliedYears
  );
  const appliedYears = useMemo(() => {
    // const currentYear = dayjs().get('year');
    const years = _appliedYearsData?.data ?? [];
    // const currentYearIndex = years.findIndex((year) => year.appliedYear === currentYear);
    // return currentYearIndex === -1 ? [{ appliedYear: currentYear }, ...years] : years;
    return years;
  }, [_appliedYearsData]);

  // mutate research kpi group
  const { mutate: mutateCreateResearchKpiGroup, isLoading: isLoadingCreateResearchKpiGroup } =
    useMutation(createResearchKpiGroup);
  const { mutate: mutateUpdateResearchKpiGroup, isLoading: isLoadingUpdateResearchKpiGroup } =
    useMutation(updateResearchKpiGroup);
  const { mutate: mutateDeleteResearchKpiGroup, isLoading: isLoadingDeleteResearchKpiGroup } =
    useMutation(deleteResearchKpiGroupById);
  const { mutate: mutateRestoreResearchKpiGroup, isLoading: isLoadingRestoreResearchKpiGroup } =
    useMutation(restoreResearchKpiGroupById);

  const { mutateAsync: mutateCloneResearchKpiGroup, isLoading: isLoadingCloneResearchKpiGroup } =
    useMutation(cloneResearchKpiGroups);

  // mutate research kpi
  const { mutate: mutateCreateResearchKpi, isLoading: isLoadingCreateResearchKpi } =
    useMutation(createResearchKpi);
  const { mutate: mutateUpdateResearchKpi, isLoading: isLoadingUpdateResearchKpi } =
    useMutation(updateResearchKpi);
  const { mutate: mutateDeleteResearchKpi, isLoading: isLoadingDeleteResearchKpi } =
    useMutation(deleteResearchKpiById);
  const { mutate: mutateRestoreResearchKpi, isLoading: isLoadingRestoreResearchKpi } =
    useMutation(restoreResearchKpiById);
  // #endregion

  const formatIndex = (value) => value.order;

  const formatResearchKpiGroupActions = (value) => (
    <div className="flex justify-content-center gap-1">
      {value?.deletedAt === null ? (
        <>
          <Can do="create" on="research_kpi">
            <Button
              className="bg-cyan-600 border-cyan-600"
              tooltip={t('researchKpi.create.title')}
              tooltipOptions={{ position: 'left' }}
              icon="pi pi-plus"
              onClick={() => {
                dialogResearchKpiFormRef.current.open({
                  mode: 'create',
                  mutate: mutateCreateResearchKpi,
                  value,
                  appliedYear: selectAppliedYear?.appliedYear,
                });
              }}
            />
          </Can>
          <Can do="update" on="research_kpi_group">
            <Button
              severity="warning"
              tooltip={t('researchKpiGroup.update.title')}
              tooltipOptions={{ position: 'left' }}
              icon="pi pi-pencil"
              onClick={() => {
                dialogResearchKpiGroupFormRef.current.open({
                  mode: 'update',
                  mutate: mutateUpdateResearchKpiGroup,
                  value,
                });
              }}
            />
          </Can>
          <Can do="delete" on="research_kpi_group">
            <Button
              severity="danger"
              tooltip={t('researchKpiGroup.delete.title')}
              tooltipOptions={{ position: 'left' }}
              icon="pi pi-trash"
              onClick={() => {
                dialogResearchKpiGroupConfirmationRef.current.open({
                  mode: 'delete',
                  mutate: mutateDeleteResearchKpiGroup,
                  value,
                  setExpandedRows,
                });
              }}
            />
          </Can>
        </>
      ) : (
        <Can not do="restore" on="research_kpi_group">
          <Button
            icon="pi pi-undo"
            severity="danger"
            tooltip={t('researchKpiGroup.restore.title')}
            tooltipOptions={{ position: 'left' }}
            onClick={() =>
              dialogResearchKpiGroupConfirmationRef.current.open({
                mode: 'restore',
                mutate: mutateRestoreResearchKpiGroup,
                value,
              })
            }
          />
        </Can>
      )}
    </div>
  );

  const formatResearchKpiActions = (value) => (
    <div className="flex justify-content-center gap-1">
      {value?.deletedAt === null ? (
        <>
          <Can do="update" on="research_kpi">
            <Button
              severity="warning"
              tooltip={t('researchKpi.update.title')}
              tooltipOptions={{ position: 'left' }}
              icon="pi pi-pencil"
              onClick={() => {
                dialogResearchKpiFormRef.current.open({
                  mode: 'update',
                  mutate: mutateUpdateResearchKpi,
                  value,
                });
              }}
            />
          </Can>
          <Can do="delete" on="research_kpi">
            <Button
              severity="danger"
              tooltip={t('researchKpi.delete.title')}
              tooltipOptions={{ position: 'left' }}
              icon="pi pi-trash"
              onClick={() => {
                dialogResearchKpiConfirmationRef.current.open({
                  mode: 'delete',
                  mutate: mutateDeleteResearchKpi,
                  value,
                });
              }}
            />
          </Can>
        </>
      ) : (
        <Can not do="restore" on="research_kpi">
          <Button
            icon="pi pi-undo"
            severity="danger"
            tooltip={t('researchKpi.restore.title')}
            tooltipOptions={{ position: 'left' }}
            onClick={() =>
              dialogResearchKpiConfirmationRef.current.open({
                mode: 'restore',
                mutate: mutateRestoreResearchKpi,
                value,
              })
            }
          />
        </Can>
      )}
    </div>
  );

  const rowExpansionTemplate = (kpiGroups) => (
    <div className="p-3">
      <DataTable
        value={kpiGroups.researchKpis}
        className="header-table"
        showGridlines
        dataKey="id"
        loading={isLoading}
        responsiveLayout="scroll"
        emptyMessage={t('researchKpi.viewList.emptyMessage')}
      >
        <Column
          body={formatIndex}
          header={t('researchKpi.viewList.no')}
          className="text-center w-5rem"
          frozen
        />
        <Column
          field="name"
          header={t('researchKpi.name')}
          className="w-30rem"
          style={{ minWidth: '14rem' }}
        />
        <Column
          field="englishName"
          header={t('researchKpi.englishName')}
          className="w-30rem"
          style={{ minWidth: '14rem' }}
        />
        <Column
          header={t('projectProposal.action')}
          body={formatResearchKpiActions}
          className="w-8rem text-center"
          frozen
          alignFrozen="right"
        />
      </DataTable>
    </div>
  );

  const allowExpansion = (rowData) => rowData.researchKpis.length > 0 && rowData.deletedAt === null;
  const handleAcceptCloneKpiGroup = useCallback(async () => {
    try {
      await mutateCloneResearchKpiGroup({
        sourceYear: appliedYears?.[1]?.appliedYear,
        destinationYear: selectAppliedYear?.appliedYear,
      });

      setConfirmDialogVisible(false);
      toast(TOAST.SUCCESS, t('researchKpiGroup.clone.success'));
      refetch();
    } catch (cloneError) {
      toast(TOAST.ERROR, mapError(t, cloneError));
    }
  }, [mutateCloneResearchKpiGroup, appliedYears, refetch]);

  const handleRejectCloneKpiGroup = useCallback(
    () => setConfirmDialogVisible(false),
    [confirmDialogVisible]
  );

  useEffect(() => {
    if (appliedYears?.length > 0) {
      setSelectAppliedYear(appliedYears[0]);
    }
  }, [appliedYears]);

  return (
    <div className="card relative">
      {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}
      <div className="flex justify-content-between align-items-center pb-4">
        <h4 className="m-0 hidden sm:block text-xl sm:text-2xl">
          {t('researchKpiGroup.viewList.title')}
        </h4>
        <div className="flex gap-3">
          <div className="flex gap-3 align-items-center">
            <h5 className="text-lg mb-0">Năm áp dụng: </h5>
            <Dropdown
              className="w-8rem"
              value={selectAppliedYear}
              optionLabel="appliedYear"
              options={appliedYears}
              onChange={(e) => setSelectAppliedYear(e.value)}
              placeholder="Năm áp dụng KIP"
            />
          </div>
          <Can do="create" on="research_kpi_group">
            {/* <Button
              className="bg-cyan-600 border-cyan-600"
              label={t('researchKpiGroup.clone.title')}
              icon="pi pi-bolt"
              tooltip={t('researchKpiGroup.clone.tooltip')}
              tooltipOptions={{ position: 'left' }}
              disabled={selectAppliedYear?.appliedYear !== appliedYears[0]?.appliedYear}
              onClick={() => {
                setConfirmDialogVisible(true);
              }}
            /> */}
            <Button
              severity="warning"
              label={t('researchKpiGroup.create.title')}
              icon="pi pi-plus"
              onClick={() => {
                dialogResearchKpiGroupFormRef.current.open({
                  mode: 'create',
                  mutate: mutateCreateResearchKpiGroup,
                  value: {
                    appliedYear: selectAppliedYear?.appliedYear,
                  },
                });
              }}
            />
          </Can>
        </div>
      </div>

      <DataTable
        value={data}
        className="p-datatable-gridlines header-table"
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        dataKey="id"
        showGridlines
        loading={isLoading}
        responsiveLayout="scroll"
        emptyMessage={t('researchKpiGroup.viewList.emptyMessage')}
      >
        <Column
          expander={allowExpansion}
          style={{ textAlign: 'center', width: '1rem', minWidth: '1rem' }}
        />
        <Column
          body={formatIndex}
          header={t('researchKpiGroup.viewList.no')}
          className="text-center w-5rem"
        />
        <Column
          field="name"
          header={t('researchKpiGroup.name')}
          className="w-30rem"
          style={{ minWidth: '14rem' }}
        />
        <Column
          field="cost"
          header={t('researchKpiGroup.cost')}
          style={{ width: '10rem', textAlign: 'center' }}
        />
        <Column
          header={t('projectProposal.action')}
          body={formatResearchKpiGroupActions}
          className="w-8rem text-center"
          frozen
          alignFrozen="right"
        />
      </DataTable>

      <ResearchKpiGroupForm
        ref={dialogResearchKpiGroupFormRef}
        isLoading={isLoadingCreateResearchKpiGroup || isLoadingUpdateResearchKpiGroup}
        refetch={refetch}
      />
      <ResearchKpiForm
        ref={dialogResearchKpiFormRef}
        isLoading={isLoadingCreateResearchKpi || isLoadingUpdateResearchKpi}
        refetch={refetch}
      />

      <ConfirmDialog
        visible={confirmDialogVisible}
        setVisible={setConfirmDialogVisible}
        onReject={handleRejectCloneKpiGroup}
        onAccept={handleAcceptCloneKpiGroup}
        content={`Bạn chắc chắn muốn sao chép toàn bộ KPI từ năm gần nhất (${appliedYears?.[1]?.appliedYear})?`}
        loading={isLoadingCloneResearchKpiGroup}
      />

      <Can do="delete" on="research_kpi_group">
        <ResearchKpiGroupConfirm
          ref={dialogResearchKpiGroupConfirmationRef}
          isLoading={isLoadingDeleteResearchKpiGroup || isLoadingRestoreResearchKpiGroup}
          refetch={refetch}
        />
      </Can>
      <Can do="delete" on="research_kpi">
        <ResearchKpiConfirm
          ref={dialogResearchKpiConfirmationRef}
          isLoading={isLoadingDeleteResearchKpi || isLoadingRestoreResearchKpi}
          refetch={refetch}
        />
      </Can>
    </div>
  );
}
